/**
 * Wurftechnik als drei unabhängige, frei kombinierbare Achsen statt einer
 * festen Liste. Eigenes Modul, weil sowohl Taktikeditor als auch
 * Granatenkatalog dieselben Werte anbieten (ersetzt die frühere
 * `utility-techniques.ts`).
 */

export const THROW_MOVEMENT_OPTIONS = [
  { value: "STAND", label: "Stand" },
  { value: "GEHEND", label: "Gehend" },
  { value: "LAUFEND", label: "Laufend" },
  { value: "DUCK", label: "Duck" },
] as const;

export const THROW_JUMP_OPTIONS = [
  { value: "OHNE", label: "Ohne Sprung" },
  { value: "JUMPTHROW", label: "Jumpthrow" },
] as const;

export const THROW_MOUSE_OPTIONS = [
  { value: "LINKSKLICK", label: "Linksklick" },
  { value: "RECHTSKLICK", label: "Rechtsklick" },
  { value: "BEIDE_MAUSTASTEN", label: "Beide Maustasten" },
] as const;

export type ThrowMovement = (typeof THROW_MOVEMENT_OPTIONS)[number]["value"];
export type ThrowJump = (typeof THROW_JUMP_OPTIONS)[number]["value"];
export type ThrowMouse = (typeof THROW_MOUSE_OPTIONS)[number]["value"];

export interface ThrowTechniqueAxes {
  movement: ThrowMovement;
  jump: ThrowJump;
  mouse: ThrowMouse;
}

export const DEFAULT_THROW_TECHNIQUE_AXES: ThrowTechniqueAxes = {
  movement: "STAND",
  jump: "OHNE",
  mouse: "LINKSKLICK",
};

const movementLabels = new Map(
  THROW_MOVEMENT_OPTIONS.map((option) => [option.value, option.label]),
);
const jumpLabels = new Map(
  THROW_JUMP_OPTIONS.map((option) => [option.value, option.label]),
);
const mouseLabels = new Map(
  THROW_MOUSE_OPTIONS.map((option) => [option.value, option.label]),
);

export function composeThrowTechnique(axes: ThrowTechniqueAxes): string {
  const parts = [movementLabels.get(axes.movement)!.toUpperCase()];
  if (axes.jump === "JUMPTHROW") {
    parts.push(jumpLabels.get(axes.jump)!.toUpperCase());
  }
  parts.push(mouseLabels.get(axes.mouse)!.toUpperCase());
  return parts.join(" + ");
}

/**
 * Rückwärtskompatibilität für die früheren 9 fest vorgegebenen Werte —
 * bestehende Datenbankeinträge sollen weiterhin korrekt vorausgewählt
 * erscheinen, auch ohne Datenmigration.
 */
const legacyTechniqueMap: Record<string, ThrowTechniqueAxes> = {
  STANDWURF: { movement: "STAND", jump: "OHNE", mouse: "LINKSKLICK" },
  JUMPTHROW: { movement: "STAND", jump: "JUMPTHROW", mouse: "LINKSKLICK" },
  RUNTHROW: { movement: "LAUFEND", jump: "OHNE", mouse: "LINKSKLICK" },
  "RUN-JUMPTHROW": {
    movement: "LAUFEND",
    jump: "JUMPTHROW",
    mouse: "LINKSKLICK",
  },
  WALKTHROW: { movement: "GEHEND", jump: "OHNE", mouse: "LINKSKLICK" },
  "WALK-JUMPTHROW": {
    movement: "GEHEND",
    jump: "JUMPTHROW",
    mouse: "LINKSKLICK",
  },
  "DUCK-WURF": { movement: "DUCK", jump: "OHNE", mouse: "LINKSKLICK" },
  RECHTSKLICK: { movement: "STAND", jump: "OHNE", mouse: "RECHTSKLICK" },
  "BEIDE MAUSTASTEN": {
    movement: "STAND",
    jump: "OHNE",
    mouse: "BEIDE_MAUSTASTEN",
  },
};

const movementValues = new Set<string>(
  THROW_MOVEMENT_OPTIONS.map((option) => option.value),
);
const jumpValues = new Set<string>(
  THROW_JUMP_OPTIONS.map((option) => option.value),
);
const mouseValues = new Set<string>(
  THROW_MOUSE_OPTIONS.map((option) => option.value),
);

function parseComposedTechnique(value: string): ThrowTechniqueAxes | null {
  const labelToValue = (
    labels: Map<string, string>,
    values: Set<string>,
    part: string | undefined,
  ) => {
    if (part === undefined) return null;
    for (const [optionValue, label] of labels) {
      if (label.toUpperCase() === part) return optionValue;
    }
    return values.has(part) ? part : null;
  };

  const parts = value.split(" + ").map((part) => part.trim());

  if (parts.length === 2) {
    const [movementPart, mousePart] = parts;
    const movement = labelToValue(movementLabels, movementValues, movementPart);
    const mouse = labelToValue(mouseLabels, mouseValues, mousePart);
    if (!movement || !mouse) return null;
    return {
      movement: movement as ThrowMovement,
      jump: "OHNE",
      mouse: mouse as ThrowMouse,
    };
  }

  if (parts.length === 3) {
    const [movementPart, jumpPart, mousePart] = parts;
    const movement = labelToValue(movementLabels, movementValues, movementPart);
    const jump = labelToValue(jumpLabels, jumpValues, jumpPart);
    const mouse = labelToValue(mouseLabels, mouseValues, mousePart);
    if (!movement || jump !== "JUMPTHROW" || !mouse) return null;
    return {
      movement: movement as ThrowMovement,
      jump: "JUMPTHROW",
      mouse: mouse as ThrowMouse,
    };
  }

  return null;
}

export function parseThrowTechnique(value: string): ThrowTechniqueAxes | null {
  const legacy = legacyTechniqueMap[value];
  if (legacy) return legacy;
  return parseComposedTechnique(value);
}

export interface ThrowTechniqueGroup {
  movement: ThrowMovement;
  movementLabel: string;
  combinations: { value: string; axes: ThrowTechniqueAxes }[];
}

/** Alle 24 Kombinationen, nach Bewegungsart gruppiert — Grundlage für die Dropdown-Optionen. */
export const throwTechniqueGroups: ThrowTechniqueGroup[] =
  THROW_MOVEMENT_OPTIONS.map(({ value: movement, label: movementLabel }) => ({
    movement,
    movementLabel,
    combinations: THROW_JUMP_OPTIONS.flatMap(({ value: jump }) =>
      THROW_MOUSE_OPTIONS.map(({ value: mouse }) => {
        const axes: ThrowTechniqueAxes = { movement, jump, mouse };
        return { value: composeThrowTechnique(axes), axes };
      }),
    ),
  }));
